import { MdAccessTime } from 'react-icons/md'
import { Temporal } from 'temporal-polyfill'
import asJqueryPlugin from '~/common/jqueryUtils'
import { useMsg } from '~/common/localizationUtils'
import { forwardRef, Stack, StackProps } from '~/design_system'
import {
  BaseDatePicker,
  BaseDatePickerProps,
  useDatePickerProps,
  useDateTimePickerProps,
  useTimePickerProps
} from '~/design_system/components/dates/DatePicker'
import useUuid from '~/hooks/useUuid'

export type LegacyDatePickerProps = BaseDatePickerProps

/**
 * LegacyDatePicker, emits a legacy JS Date.
 * @deprecated Use `DatePicker` instead which emits a Temporal.PlainDate.
 */
export const LegacyDatePicker = forwardRef<LegacyDatePickerProps, 'input'>((props, ref) => {
  return <BaseDatePicker ref={ref} {...useDatePickerProps()} {...props} />
})
LegacyDatePicker.displayName = 'LegacyDatePicker'

asJqueryPlugin('DatePicker', LegacyDatePicker)

/**
 * LegacyDateTimePicker, emits a legacy JS Date.
 * @deprecated Use `DateTimePicker` instead which emits a Temporal.PlainDateTime.
 */
export const LegacyDateTimePicker = forwardRef<LegacyDatePickerProps, 'input'>((props, ref) => {
  return <BaseDatePicker ref={ref} {...useDateTimePickerProps()} {...props} />
})
LegacyDateTimePicker.displayName = 'LegacyDateTimePicker'

/**
 * LegacyTimePicker, emits a legacy JS Date.
 * @deprecated Use `TimePicker` instead which emits a Temporal.PlainTime.
 */
export const LegacyTimePicker = forwardRef<LegacyDatePickerProps, 'input'>((props, ref) => {
  return <BaseDatePicker ref={ref} {...useTimePickerProps()} {...props} />
})
LegacyTimePicker.displayName = 'LegacyTimePicker'

/** Date range value object. Contains `from` and `to` values. */
export type DateRangeValue = {
  from: Date | null
  to: Date | null
}

export type DateRangePickerProps = {
  /** The `id` attribute used to construct ids, aria-labels and data-testids. */
  label?: string
  /** The `from` and `to` date range values object. */
  value: DateRangeValue
  /** Change handler for a date range values object. */
  onChange: (value: DateRangeValue) => void
  isClearable?: boolean
  disabled?: boolean
} & Omit<StackProps, 'onChange'>

/**
 * Renders two `DatePicker` inputs that are configured to accept and return a
 * `{from: Date, to: Date}` type object, allowing the user to select a range of
 * dates.
 */
export const LegacyDateRangePicker = ({
  label,
  onChange,
  value,
  disabled,
  isClearable,
  ...props
}: DateRangePickerProps) => {
  const msg = useMsg()
  const uuid = useUuid()
  const idPrefix = label ? `${label.toLowerCase().split(' ').join('-')}-` : `${uuid}-`
  return (
    <Stack {...props}>
      <LegacyDatePicker
        name={`${idPrefix}from`}
        onChange={date => onChange({ from: date, to: value.to })}
        selected={value.from}
        selectsStart
        startDate={value.from ?? undefined}
        endDate={value.to ?? undefined}
        maxDate={value.to ?? undefined}
        placeholderText={msg('datepicker.date_range.from')}
        disabled={disabled}
        isClearable={isClearable}
        data-testid={`${idPrefix}from`}
        inputProps={{ 'aria-label': `${label} ${msg('datepicker.date_range.range_from')}` }}
      />
      <LegacyDatePicker
        name={`${idPrefix}to`}
        onChange={date => onChange({ from: value.from, to: date })}
        selected={value.to}
        selectsEnd
        startDate={value.from ?? undefined}
        endDate={value.to ?? undefined}
        minDate={value.from ?? undefined}
        placeholderText={msg('datepicker.date_range.to')}
        disabled={disabled}
        isClearable={isClearable}
        data-testid={`${idPrefix}to`}
        inputProps={{ 'aria-label': `${label} ${msg('datepicker.date_range.range_to')}` }}
      />
    </Stack>
  )
}

export type TimeRangePickerProps = {
  label?: string
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
  isClearable?: boolean
  disabled?: boolean
} & Omit<StackProps, 'onChange'>

/**
 * Renders two `TimePicker` inputs that are configured to accept and return a
 * `{from: Time, to: Time}` type object, allowing the user to select a range of
 * times.
 */
export const LegacyTimeRangePicker = ({
  label,
  onChange,
  value,
  disabled,
  isClearable,
  ...props
}: TimeRangePickerProps) => {
  const msg = useMsg()
  const uuid = useUuid()

  const today = Temporal.Now.zonedDateTimeISO()
  const todayBeginning = new Date(today.startOfDay().epochMilliseconds)
  const todayEnd = new Date(today.with({ hour: 23, minute: 59, second: 59, millisecond: 999 }).epochMilliseconds)

  const idPrefix = label ? `${label.toLowerCase().split(' ').join('-')}-` : `${uuid}-`

  const timeProps = {
    inputIcon: MdAccessTime,
    timeIntervals: 15,
    timeCaption: msg('datepicker.time'),
    showTimeSelect: true,
    showTimeSelectOnly: true,
    showMonthYearPicker: false
  }
  return (
    <Stack {...props}>
      <LegacyTimePicker
        name="from"
        {...timeProps}
        onChange={time => onChange({ from: time, to: value.to })}
        selected={value.from}
        placeholderText="From"
        disabled={disabled}
        isClearable={isClearable}
        minTime={todayBeginning}
        maxTime={value.to ?? todayEnd}
        data-testid={`${idPrefix}from`}
        inputProps={{ 'aria-label': `${label} ${msg('datepicker.date_range.range_from')}` }}
      />
      <LegacyTimePicker
        name="to"
        {...timeProps}
        onChange={time => onChange({ from: value.from, to: time })}
        selected={value.to}
        placeholderText="To"
        disabled={disabled}
        isClearable={isClearable}
        minTime={value.from ?? todayBeginning}
        maxTime={todayEnd}
        data-testid={`${idPrefix}to`}
        inputProps={{ 'aria-label': `${label} ${msg('datepicker.date_range.range_to')}` }}
      />
    </Stack>
  )
}
